/**
 * Webhook Delivery Log Page (Sprint C14 Lane B WP B-4)
 *
 * Tenant-scoped read view of HMAC-signed outbound webhook deliveries.
 *
 * Backend: GET /api/admin/webhook-deliveries[/{id}|/health] (platform-admin-api,
 * canonical spec at schema-registry/webhook-delivery-log.openapi.yaml).
 *
 * Layout (Financial Times / Economist aesthetic, per packages/ui guidance):
 *   - Header: title + manual refresh button.
 *   - 4 metric tiles: success rate / p50 / p95 / retry count (24h window).
 *   - Filter bar: status enum + eventType select over WebhookEventType,
 *     labeled (fi-30) + since date (a date picker; the day becomes the
 *     spec's date-time `since`).
 *   - DataGrid: timestamp, eventType, status badge, endpointId (truncated),
 *     attemptNumber, latencyMs.
 *   - Drill-down Sheet: HMAC signature, retry chain timeline, response body
 *     (with 4KB truncation indicator), request payload JSON.
 *
 * Polling: list + health refetch on a 10s interval (real-time WebSocket is the
 * C15-1 follow-on per sprint README).
 *
 * Empty state: honest -- no producers yet (Sprint C15+ alerting/monitoring jobs
 * will populate the table). successRate, p50LatencyMs and p95LatencyMs are
 * nullable in the contract (ruling absence-representable-contracts): the
 * endpoint answers null for a window with no delivery, and null latencies when
 * no attempt recorded one. A null tile prints `--` and says why. The tiles
 * also print `--` when the list read proves the window empty
 * (healthWindowIsEmpty below).
 *
 * Route: /admin/webhook-deliveries
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  Clock,
  RefreshCw,
  Webhook,
  Zap,
} from 'lucide-react';
import {
  Button,
  Card,
  DataGrid,
  EmptyState,
  FilterBar,
  METRIC_READ_CAPTIONS,
  MetricCard,
  MetricCardGrid,
  Sheet,
  Skeleton,
} from '@aaimplatform/ui';
import type {
  ActiveFilter,
  ColumnDef,
  FilterDefinition,
} from '@aaimplatform/ui';
import {
  useWebhookDeliveries,
  useWebhookDeliveryDetail,
  useWebhookDeliveryHealth,
} from '../hooks/useWebhookDeliveryLog';
import { useWebhookEndpoints } from '../hooks/useWebhookEndpoints';
import type {
  WebhookDeliveryListQuery,
  WebhookDeliveryStatus,
  WebhookDeliverySummary,
  WebhookRetryChainEntry,
} from '../api/services';
import {
  WEBHOOK_EVENT_TYPES,
  WEBHOOK_EVENT_TYPE_LABELS,
  isWebhookEventType,
  type WebhookEventType,
} from '../utils/webhook-event-types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE = 50;
const RESPONSE_BODY_TRUNCATE_BYTES = 4096;

const STATUS_OPTIONS: ReadonlyArray<{ value: WebhookDeliveryStatus; label: string }> = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'RETRYING', label: 'Retrying' },
];

const FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: STATUS_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label })),
  },
  {
    // One event the platform emits, chosen by label from WebhookEventType
    // (fi-30). It was free text that sent any string, and "pledge.submitted"
    // is emitted by nothing (fi-g4-8). The list operation's eventType
    // parameter takes one WebhookEventType, so this is a single select.
    id: 'eventType',
    label: 'Event type',
    type: 'select',
    options: WEBHOOK_EVENT_TYPES.map((value) => ({ value, label: WEBHOOK_EVENT_TYPE_LABELS[value] })),
  },
  {
    // A date picker, not a free-text ISO-8601 field whose raw string went to
    // the API unchecked (fi-g4-10). sinceFromDay turns the picked day into the
    // date-time the spec's `since` parameter takes.
    id: 'since',
    label: 'Since',
    type: 'date',
  },
];

// ---------------------------------------------------------------------------
// Formatters / helpers
// ---------------------------------------------------------------------------

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatLatency(latencyMs: number | null | undefined): string {
  if (latencyMs === null || latencyMs === undefined) return '--';
  if (latencyMs < 1000) return `${latencyMs} ms`;
  return `${(latencyMs / 1000).toFixed(2)} s`;
}

function formatPercent(fraction: number): string {
  return `${(fraction * 100).toFixed(1)}%`;
}

function truncateMiddle(value: string, head = 8, tail = 6): string {
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}...${value.slice(-tail)}`;
}

function getStatusBadgeClass(status: WebhookDeliveryStatus): string {
  switch (status) {
    case 'DELIVERED':
      return 'bg-growth/10 text-growth';
    case 'FAILED':
      return 'bg-error/10 text-error';
    case 'RETRYING':
      return 'bg-warning/10 text-warning';
    case 'PENDING':
      return 'bg-tertiary/10 text-tertiary';
  }
}

function statusLabel(status: WebhookDeliveryStatus): string {
  return STATUS_OPTIONS.find((opt) => opt.value === status)?.label ?? status;
}

const HEALTH_WINDOW_EMPTY_CAPTION = 'No deliveries in the last 24h';
const HEALTH_NO_LATENCY_CAPTION = 'No latency recorded in the last 24h';

/**
 * True only when the list read PROVES the health window holds no delivery, so
 * the health endpoint's 0 success rate and 0 ms latencies are not measurements.
 *
 * The proof needs an unfiltered first page, because the server orders the list
 * newest first (webhook-delivery-log.routes.ts, orderBy createdAt desc): an
 * empty page, or a newest row older than the window, means nothing landed in
 * the window. Any filter, a later cursor, or a failed read proves nothing, and
 * the tiles then print what the server reported.
 */
export function healthWindowIsEmpty({
  listSucceeded,
  query,
  items,
  windowHours,
  now,
}: {
  listSucceeded: boolean;
  query: WebhookDeliveryListQuery;
  items: ReadonlyArray<Pick<WebhookDeliverySummary, 'createdAt'>>;
  windowHours: number;
  now: number;
}): boolean {
  if (!listSucceeded) return false;
  if (query.cursor || query.status || query.eventType || query.since) return false;
  if (items.length === 0) return true;
  const newest = Date.parse(items[0].createdAt);
  if (Number.isNaN(newest)) return false;
  return newest < now - windowHours * 60 * 60 * 1000;
}

function statusFromFilter(filters: ActiveFilter[]): WebhookDeliveryStatus | undefined {
  const entry = filters.find((f) => f.filterId === 'status');
  if (!entry) return undefined;
  const candidate = entry.value as string;
  return STATUS_OPTIONS.some((opt) => opt.value === candidate)
    ? (candidate as WebhookDeliveryStatus)
    : undefined;
}

/** The event-type filter's value when it is a WebhookEventType; anything else is not sent. */
export function eventTypeFromFilter(filters: ActiveFilter[]): WebhookEventType | undefined {
  const candidate = filters.find((f) => f.filterId === 'eventType')?.value;
  return isWebhookEventType(candidate) ? candidate : undefined;
}

function readFilterValue(filters: ActiveFilter[], id: string): string | undefined {
  const entry = filters.find((f) => f.filterId === id);
  if (!entry) return undefined;
  const value = String(entry.value ?? '').trim();
  return value.length > 0 ? value : undefined;
}

const DAY_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The picked day ("2026-09-27") as the ISO 8601 instant the `since` query
 * parameter takes (webhook-delivery-log.openapi.yaml, format date-time): the
 * start of that day in the viewer's zone, so "since the 27th" includes every
 * delivery the viewer would call the 27th. Anything but a valid day is
 * dropped rather than sent.
 */
export function sinceFromDay(day: string | undefined): string | undefined {
  if (!day) return undefined;
  const match = DAY_ONLY.exec(day);
  if (!match) return undefined;
  const [, year, month, date] = match;
  const start = new Date(Number(year), Number(month) - 1, Number(date));
  if (
    start.getFullYear() !== Number(year) ||
    start.getMonth() !== Number(month) - 1 ||
    start.getDate() !== Number(date)
  ) {
    return undefined;
  }
  return start.toISOString();
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const COLUMNS: ColumnDef<WebhookDeliverySummary, unknown>[] = [
  {
    accessorKey: 'createdAt',
    header: 'Timestamp',
    cell: ({ getValue }) => (
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-tertiary flex-shrink-0" />
        <span className="text-sm font-mono text-primary tabular-nums leading-normal">
          {formatTimestamp(getValue() as string)}
        </span>
      </div>
    ),
    size: 200,
  },
  {
    accessorKey: 'eventType',
    header: 'Event',
    cell: ({ getValue }) => (
      <span className="text-sm font-mono text-accent leading-normal">
        {getValue() as string}
      </span>
    ),
    size: 220,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue() as WebhookDeliveryStatus;
      return (
        <span
          className={`inline-flex items-center px-2 py-1 text-xs font-sans font-medium rounded-sm leading-normal ${getStatusBadgeClass(status)}`}
        >
          {statusLabel(status)}
        </span>
      );
    },
    size: 120,
  },
  {
    accessorKey: 'endpointId',
    header: 'Endpoint',
    cell: ({ getValue }) => (
      <span className="text-xs font-mono text-tertiary tabular-nums leading-normal">
        {truncateMiddle(getValue() as string)}
      </span>
    ),
    size: 180,
  },
  {
    accessorKey: 'attemptNumber',
    header: 'Attempt',
    cell: ({ getValue }) => (
      <span className="text-sm font-sans tabular-nums text-primary leading-normal">
        #{getValue() as number}
      </span>
    ),
    size: 100,
  },
  {
    accessorKey: 'latencyMs',
    header: 'Latency',
    cell: ({ getValue }) => (
      <span className="text-sm font-mono tabular-nums text-secondary leading-normal">
        {formatLatency(getValue() as number | null)}
      </span>
    ),
    size: 120,
  },
  {
    accessorKey: 'responseStatus',
    header: 'HTTP',
    cell: ({ getValue }) => {
      const value = getValue() as number | null;
      return (
        <span className="text-sm font-mono tabular-nums text-tertiary leading-normal">
          {value === null || value === undefined ? '--' : String(value)}
        </span>
      );
    },
    size: 90,
  },
];

// ---------------------------------------------------------------------------
// Drill-down panel content
// ---------------------------------------------------------------------------

interface RetryChainTimelineProps {
  chain: ReadonlyArray<WebhookRetryChainEntry>;
  highlightedId: string;
}

function RetryChainTimeline({ chain, highlightedId }: RetryChainTimelineProps) {
  if (chain.length === 0) {
    return (
      <p className="text-sm font-sans text-tertiary leading-normal">
        No sibling attempts recorded.
      </p>
    );
  }
  return (
    <ol className="space-y-2">
      {chain.map((entry, index) => {
        const isCurrent = entry.id === highlightedId;
        return (
          <li
            key={entry.id}
            className={`flex items-center gap-3 px-3 py-2 rounded-sm border ${
              isCurrent ? 'border-accent bg-accent/5' : 'border-recessed bg-surface'
            }`}
          >
            <span
              className="w-6 text-xs font-mono tabular-nums text-tertiary text-right"
              aria-label={`Position ${index + 1} of ${chain.length}`}
            >
              {index + 1}
            </span>
            <span className="text-xs font-mono tabular-nums text-primary">
              attempt #{entry.attemptNumber}
            </span>
            <span
              className={`inline-flex items-center px-2 py-0.5 text-xs font-sans font-medium rounded-sm leading-normal ${getStatusBadgeClass(entry.status)}`}
            >
              {entry.status}
            </span>
            <span className="text-xs font-mono tabular-nums text-secondary">
              {entry.responseStatus === null || entry.responseStatus === undefined
                ? '--'
                : `HTTP ${entry.responseStatus}`}
            </span>
            <span className="text-xs font-mono tabular-nums text-tertiary">
              {formatLatency(entry.latencyMs ?? null)}
            </span>
            <span className="text-xs font-mono tabular-nums text-tertiary ml-auto">
              {formatTimestamp(entry.createdAt)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

interface DrillDownContentProps {
  id: string;
}

function DrillDownContent({ id }: DrillDownContentProps) {
  const { data, isLoading, error } = useWebhookDeliveryDetail(id);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-error/5 border border-error/20 rounded-sm p-4 flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 text-error flex-shrink-0" />
        <div className="text-sm font-sans text-error">
          {error instanceof Error ? error.message : 'Failed to load delivery detail.'}
        </div>
      </div>
    );
  }

  const responseBody = data.responseBody ?? '';
  const isResponseBodyTruncated =
    data.responseBody !== null &&
    data.responseBody !== undefined &&
    data.responseBody.length === RESPONSE_BODY_TRUNCATE_BYTES;

  return (
    <div className="space-y-6">
      {/* Header summary */}
      <section>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-sans text-tertiary uppercase tracking-wider">
              Event
            </div>
            <div className="text-sm font-mono text-accent leading-normal">
              {data.eventType}
            </div>
          </div>
          <span
            className={`inline-flex items-center px-2 py-1 text-xs font-sans font-medium rounded-sm leading-normal ${getStatusBadgeClass(data.status)}`}
          >
            {data.status}
          </span>
        </div>
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <dt className="text-xs font-sans text-tertiary">Endpoint ID</dt>
            <dd className="font-mono text-primary text-xs break-all">{data.endpointId}</dd>
          </div>
          <div>
            <dt className="text-xs font-sans text-tertiary">Attempt</dt>
            <dd className="font-mono text-primary tabular-nums">#{data.attemptNumber}</dd>
          </div>
          <div>
            <dt className="text-xs font-sans text-tertiary">Response</dt>
            <dd className="font-mono text-primary tabular-nums">
              {data.responseStatus === null || data.responseStatus === undefined
                ? '--'
                : `HTTP ${data.responseStatus}`}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-sans text-tertiary">Latency</dt>
            <dd className="font-mono text-primary tabular-nums">
              {formatLatency(data.latencyMs ?? null)}
            </dd>
          </div>
          <div className="col-span-2">
            <dt className="text-xs font-sans text-tertiary">Timestamp</dt>
            <dd className="font-mono text-primary">{formatTimestamp(data.createdAt)}</dd>
          </div>
        </dl>
      </section>

      {/* HMAC signature */}
      <section>
        <h3 className="text-xs font-sans uppercase tracking-wider text-tertiary mb-2">
          HMAC-SHA256 signature
        </h3>
        <pre className="text-xs font-mono text-primary bg-elevated border border-recessed rounded-sm p-3 overflow-x-auto break-all whitespace-pre-wrap">
          {data.hmacSignature}
        </pre>
      </section>

      {/* Retry chain */}
      <section>
        <h3 className="text-xs font-sans uppercase tracking-wider text-tertiary mb-2">
          Retry chain ({data.retryChain.length})
        </h3>
        <RetryChainTimeline chain={data.retryChain} highlightedId={data.id} />
      </section>

      {/* Request payload */}
      <section>
        <h3 className="text-xs font-sans uppercase tracking-wider text-tertiary mb-2">
          Request payload
        </h3>
        <pre className="text-xs font-mono text-primary bg-elevated border border-recessed rounded-sm p-3 overflow-x-auto whitespace-pre-wrap max-h-72">
          {JSON.stringify(data.requestPayload, null, 2)}
        </pre>
      </section>

      {/* Response body */}
      <section>
        <h3 className="text-xs font-sans uppercase tracking-wider text-tertiary mb-2 flex items-center justify-between">
          <span>Response body</span>
          {isResponseBodyTruncated && (
            <span className="text-xs font-sans normal-case tracking-normal text-warning">
              Truncated to first 4 KB
            </span>
          )}
        </h3>
        <pre className="text-xs font-mono text-primary bg-elevated border border-recessed rounded-sm p-3 overflow-x-auto whitespace-pre-wrap max-h-72">
          {responseBody.length > 0 ? responseBody : '(empty)'}
        </pre>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function WebhookDeliveryLogPage() {
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [drillDownId, setDrillDownId] = useState<string | null>(null);
  // Cursor pagination: history of cursors visited so the user can step back.
  // Wave-2 follow-on may switch to a typed CursorPaginatedQuery hook.
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const listQuery: WebhookDeliveryListQuery = useMemo(() => {
    return {
      cursor,
      limit: PAGE_SIZE,
      status: statusFromFilter(activeFilters),
      eventType: eventTypeFromFilter(activeFilters),
      since: sinceFromDay(readFilterValue(activeFilters, 'since')),
    };
  }, [cursor, activeFilters]);

  const {
    data: list,
    isLoading: listLoading,
    isSuccess: listSucceeded,
    error: listError,
    refetch: refetchList,
  } = useWebhookDeliveries(listQuery);
  const {
    data: health,
    isLoading: healthLoading,
    error: healthError,
    refetch: refetchHealth,
  } = useWebhookDeliveryHealth();
  // Whether any endpoint is registered decides why an empty log is empty
  // (fi-g4-9). Only a successful read can say there is none.
  const endpointsQuery = useWebhookEndpoints();
  const noEndpointRegistered =
    endpointsQuery.isSuccess && endpointsQuery.data.data.length === 0;

  const items = list?.items ?? [];
  const nextCursor = list?.nextCursor;

  const windowEmpty = healthWindowIsEmpty({
    listSucceeded,
    query: listQuery,
    items,
    windowHours: health?.windowHours ?? 24,
    now: Date.now(),
  });
  const serverFiltersActive = Boolean(
    listQuery.status || listQuery.eventType || listQuery.since || listQuery.cursor,
  );
  // A withheld tile prints `--` and says why; a failed health read is not a 0.
  const healthCaption = !health && healthError ? METRIC_READ_CAPTIONS.failed : undefined;
  // A null successRate is the endpoint saying no delivery was attempted in the
  // window; a null latency with deliveries present means none recorded one.
  const noDeliveries = windowEmpty || (!!health && health.successRate === null);
  const measuredCaption = noDeliveries ? HEALTH_WINDOW_EMPTY_CAPTION : healthCaption;
  const successRate = health && !windowEmpty ? health.successRate : null;
  const p50LatencyMs = health && !windowEmpty ? health.p50LatencyMs : null;
  const p95LatencyMs = health && !windowEmpty ? health.p95LatencyMs : null;
  const latencyCaption = (latency: number | null): string | undefined =>
    health && !noDeliveries && latency === null ? HEALTH_NO_LATENCY_CAPTION : measuredCaption;

  // Apply a cheap client-side text search across the loaded page so operators
  // can narrow without hitting the API for every keystroke. Server-side
  // filters (status / eventType / since) above are authoritative.
  const filteredItems = useMemo(() => {
    if (searchQuery.length === 0) return items;
    const q = searchQuery.toLowerCase();
    return items.filter((row) => {
      return [
        row.eventType,
        row.endpointId,
        row.id,
        row.tenantId,
        row.status,
        String(row.responseStatus ?? ''),
        String(row.attemptNumber),
      ]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [items, searchQuery]);

  const handleNextPage = () => {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, cursor ?? '']);
    setCursor(nextCursor);
  };

  const handlePrevPage = () => {
    setCursorStack((prev) => {
      if (prev.length === 0) {
        setCursor(undefined);
        return prev;
      }
      const next = prev.slice(0, -1);
      const restored = prev[prev.length - 1];
      setCursor(restored.length > 0 ? restored : undefined);
      return next;
    });
  };

  const handleResetFilters = (next: ActiveFilter[]) => {
    setActiveFilters(next);
    // Any filter change resets pagination -- cursors are bound to the prior
    // filter shape and would be invalid against the new server-side query.
    setCursor(undefined);
    setCursorStack([]);
  };

  const handleRefresh = () => {
    refetchList();
    refetchHealth();
  };

  return (
    <div>
      {/* Page heading */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          {/* Page title lives in the sticky topnav (AppHeader); the body opens
              with the descriptive subtitle, no redundant H1. */}
          <p className="text-sm font-sans text-tertiary">
            HMAC-signed outbound deliveries to your webhook endpoints. Polls every 10s.
          </p>
        </div>
        <Button variant="secondary" onClick={handleRefresh} title="Refresh now">
          <RefreshCw className={`w-4 h-4 ${listLoading || healthLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Health metrics (24h) */}
      <MetricCardGrid className="mb-8">
        {healthLoading && !health ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-surface p-6 rounded-sm shadow-sm border border-recessed"
            >
              <Skeleton className="h-10 w-16 mb-3" />
              <Skeleton className="h-3 w-24" />
            </div>
          ))
        ) : (
          <>
            <MetricCard
              label="Success rate (24h)"
              value={successRate !== null ? formatPercent(successRate) : '--'}
              caption={measuredCaption}
              description="Share of attempts delivered"
            />
            <MetricCard
              label="p50 latency"
              value={p50LatencyMs !== null ? `${p50LatencyMs} ms` : '--'}
              caption={latencyCaption(p50LatencyMs)}
              description="Median round-trip"
            />
            <MetricCard
              label="p95 latency"
              value={p95LatencyMs !== null ? `${p95LatencyMs} ms` : '--'}
              caption={latencyCaption(p95LatencyMs)}
              description="95th percentile"
            />
            <MetricCard
              label="Retries (24h)"
              value={health ? String(health.retryCount) : '--'}
              caption={healthCaption}
              description="Attempts after the first"
              changeType={health && health.retryCount > 0 ? 'negative' : 'neutral'}
            />
          </>
        )}
      </MetricCardGrid>

      {/* Error state */}
      {listError && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            {listError instanceof Error ? listError.message : 'Failed to load deliveries'}
            <button
              onClick={() => refetchList()}
              className="ml-3 font-semibold text-accent hover:underline"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <FilterBar
        filters={FILTER_DEFINITIONS}
        activeFilters={activeFilters}
        onFiltersChange={handleResetFilters}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search this page by event, endpoint, status or HTTP code"
        className="rounded-t-sm"
      />

      {/* Data grid + empty state. A failed read with nothing loaded renders the
          error above and no empty state: only a successful read can say the
          log is empty (fi-g4-15). */}
      {listError && items.length === 0 ? null : listLoading && items.length === 0 ? (
        <div className="bg-surface rounded-b-sm shadow-sm p-8">
          <Skeleton className="h-8 w-full mb-4" />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full mb-2" />
          ))}
        </div>
      ) : filteredItems.length === 0 ? (
        // The kit EmptyState every other list page uses; this page built its
        // own icon and paragraphs (r3 fi-cons-6).
        <Card className="rounded-t-none">
          {items.length > 0 ? (
            <EmptyState
              icon={<Webhook className="h-8 w-8" />}
              title="No deliveries on this page match the search"
            />
          ) : serverFiltersActive ? (
            <EmptyState
              icon={<Webhook className="h-8 w-8" />}
              title="No deliveries match these filters"
            />
          ) : noEndpointRegistered ? (
            <EmptyState
              icon={<Webhook className="h-8 w-8" />}
              title="No webhook deliveries yet"
              description="No webhook endpoint is registered, so nothing is delivered."
              action={
                <Link
                  to="/admin/config/webhook-endpoints"
                  className="text-sm font-sans text-accent hover:underline"
                >
                  Register an endpoint
                </Link>
              }
            />
          ) : (
            <EmptyState
              icon={<Webhook className="h-8 w-8" />}
              title="No webhook deliveries yet"
              description="Outbound webhook deliveries will appear here once an alerting, monitoring, or pledge-event job emits one."
            />
          )}
        </Card>
      ) : (
        <DataGrid<WebhookDeliverySummary>
          data={filteredItems}
          columns={COLUMNS}
          enableColumnResize
          hoverable
          striped
          getRowId={(row) => row.id}
          onRowClick={(row) => setDrillDownId(row.id)}
          className="rounded-b-sm shadow-sm"
        />
      )}

      {/* Cursor pagination footer (only when >1 page or on a non-first page) */}
      {(nextCursor || cursorStack.length > 0) && (
        <div className="flex items-center justify-between mt-4 px-2">
          <button
            onClick={handlePrevPage}
            disabled={cursorStack.length === 0}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-sans text-primary bg-surface border border-recessed rounded-sm hover:bg-elevated disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <span className="text-xs font-sans text-tertiary">
            Showing {filteredItems.length} of {items.length} loaded
            {searchQuery.length > 0 && ' (page-local search applied)'}
          </span>
          <button
            onClick={handleNextPage}
            disabled={!nextCursor}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-sans text-primary bg-surface border border-recessed rounded-sm hover:bg-elevated disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next
            <Zap className="w-3 h-3" aria-hidden />
          </button>
        </div>
      )}

      {/* Drill-down sheet */}
      <Sheet
        open={drillDownId !== null}
        onOpenChange={(open) => {
          if (!open) setDrillDownId(null);
        }}
        side="right"
        size="xl"
        title="Webhook delivery"
        description="HMAC signature, retry chain, payload, and response body for this attempt."
      >
        {drillDownId !== null ? (
          <DrillDownContent id={drillDownId} />
        ) : (
          <div className="flex items-center gap-2 text-sm font-sans text-tertiary">
            <Activity className="w-4 h-4" />
            Select a delivery to inspect.
          </div>
        )}
      </Sheet>
    </div>
  );
}
